'use client';

// cSpell:ignore subtitl

import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useMergedRefs } from '@/hooks/useMergedRefs';
import { useValidExposure } from '@/hooks/useValidExposure';
import { useSpmDomAttr, withSpmCnt } from '@/reporting/spm';
import { useHomeSpmModuleCode } from '@/app/(home-page)/utils/spm-module';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import type {
  OneLineTwoContent,
  OneLineTwoItem,
  OneLineTwoProps,
  OneLineTwoTrackingParams,
} from './types';

const COMPONENT_ID = 'oneLineTwo2026';
const DEFAULT_TITLE_COLOR = 'var(--color-primary-black)';
const DEFAULT_CARD_TEXT_COLOR = 'var(--color-primary-white)';
const DEFAULT_SUBTITLE_COLOR = '#666666';

const formatText = (text?: string) => text?.replace(/<br>/g, '\n') || '';

const getSubtitleColor = (content: OneLineTwoContent) =>
  content.layout_subtitle_color || content.layout_subtitl_color || DEFAULT_SUBTITLE_COLOR;

const getImageSize = (value: string | number | undefined, fallback: number) => {
  const size = Number(value);
  return Number.isFinite(size) && size > 0 ? size : fallback;
};

const getAlt = (index: number, item: OneLineTwoItem, moduleTitle: string) =>
  (item.title && `${item.title} Glasses`) ||
  item.sku?.short_name ||
  item.product_group?.name ||
  `${moduleTitle || COMPONENT_ID} ${index + 1}`;

const buildTrackingParams = (
  item: OneLineTwoItem,
  originalIndex: number,
  componentId: string,
  moduleId: string,
  moduleTitle: string,
  moduleIndex: number,
): OneLineTwoTrackingParams => ({
  module_index: moduleIndex,
  module_name: componentId,
  module_id: moduleId,
  mkt_index: originalIndex,
  mkt_index_sub: 0,
  mkt_jump_type: String(item.jump_type || ''),
  mkt_jump_content: String(item.Jump_target || ''),
  module_tab_name: '',
  coupon_id_list: '',
  coupon_name_list: '',
  shop_sku: String(item.sku?.shop_sku || item.shop_sku || ''),
  business_sku: String(item.sku?.business_sku || ''),
  product_sku: String(item.sku?.sku || ''),
  product_id: String(item.sku?.id || ''),
  product_name: String(item.sku?.short_name || ''),
  group_id: String(item.product_group?.id || ''),
  group_name: String(item.product_group?.name || ''),
  module_title: moduleTitle,
});

const buildJumpProps = (item: OneLineTwoItem) => ({
  jump_type: item.jump_type,
  Jump_target: item.Jump_target,
  ...(item.sku?.shop_sku && { sku: { shop_sku: item.sku.shop_sku } }),
  ...(item.product_group?.id && {
    product_group: { id: String(item.product_group.id), path: item.product_group.path },
  }),
});

const useTrackingHandlers = (
  trackingParams: OneLineTwoTrackingParams,
  spmModule: string,
  onTrack?: (params: OneLineTwoTrackingParams) => void,
  onExpTrack?: (params: OneLineTwoTrackingParams) => void,
  onValidExpTrack?: (params: OneLineTwoTrackingParams) => void,
  clickSpmCnt?: string,
  exposureSpmCnt?: string,
) => {
  const [expRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      onExpTrack?.(trackingParams);
    }
  }, [inView, onExpTrack, trackingParams]);

  const handleClick = useCallback(() => {
    // spm_cnt 为坑位完整编码（D 位）；浅拷贝避免污染曝光/点击共享的 trackingParams。
    onTrack?.(clickSpmCnt ? withSpmCnt(trackingParams, clickSpmCnt) : trackingParams);
  }, [clickSpmCnt, onTrack, trackingParams]);

  const { ref: validExposureRef } = useValidExposure({
    // 单卡片有效曝光为 item 级，spm_cnt 追加坑位 D 位（动态风格位，序号 2 位：style_entry_01）。
    callback: () => onValidExpTrack?.(withSpmCnt(trackingParams, exposureSpmCnt ?? spmModule)),
  });

  return { expRef, handleClick, validExposureRef };
};

function OneLineTwoCard({
  item,
  originalIndex,
  componentId,
  moduleId,
  moduleTitle,
  moduleIndex,
  spmModule,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: {
  item: OneLineTwoItem;
  originalIndex: number;
  componentId: string;
  moduleId: string;
  moduleTitle: string;
  moduleIndex: number;
  /** 楼层完整动态 SPM 模块编码（C 位，`${component_id}_${id}`）。 */
  spmModule: string;
  onTrack?: (params: OneLineTwoTrackingParams) => void;
  onExpTrack?: (params: OneLineTwoTrackingParams) => void;
  onValidExpTrack?: (params: OneLineTwoTrackingParams) => void;
}) {
  const trackingParams = useMemo(
    () => buildTrackingParams(item, originalIndex, componentId, moduleId, moduleTitle, moduleIndex),
    [componentId, item, moduleId, moduleIndex, moduleTitle, originalIndex],
  );
  // 坑位 D 位编码（动态风格位，序号 2 位：style_entry_01）：点击与 item 级有效曝光共用；
  // 模块码缺失（表外旧数据）时为空串，不硬造。
  const styleEntrySpmCnt = spmModule
    ? `${spmModule}.style_entry_${String(originalIndex + 1).padStart(2, '0')}`
    : '';
  // Data SPM DOM 打标（请求头 data-spm=1 开关）：与上报 spm_cnt 同值，表外静默不打标。
  const styleEntryDomSpm = useSpmDomAttr(styleEntrySpmCnt);
  // 获取埋点处理函数（点击与 item 级有效曝光均为坑位完整编码：style_entry + 序号 2 位）。
  const { expRef, handleClick, validExposureRef } = useTrackingHandlers(
    trackingParams,
    spmModule,
    onTrack,
    onExpTrack,
    onValidExpTrack,
    styleEntrySpmCnt,
    styleEntrySpmCnt,
  );
  const cardRef = useMergedRefs(expRef, validExposureRef);

  const jumpProps = useMemo(() => buildJumpProps(item), [item]);
  const titleColor = item.title_color || DEFAULT_CARD_TEXT_COLOR;
  const titleColorStyle = useMemo(() => ({ color: titleColor }), [titleColor]);
  const alt = getAlt(originalIndex, item, moduleTitle);
  const imageWidth = getImageSize(item.jump_img_width, 370);
  const imageHeight = getImageSize(item.jump_img_height, 500);

  return (
    <div ref={cardRef} className="min-w-0">
      <MagicJump
        prefetch
        item={jumpProps}
        onBeforeJump={handleClick}
        aria-label={alt}
        className="block overflow-hidden rounded-[6px]"
        {...styleEntryDomSpm}
      >
        <div className="relative aspect-[477/592] w-full overflow-hidden bg-auxiliary-light-gray">
          <Image
            src={item.jump_image || ''}
            alt={alt}
            width={imageWidth}
            height={imageHeight}
            loading={moduleIndex < 3 ? 'eager' : 'lazy'}
            className="h-full w-full object-cover"
          />
          {item.title && (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent px-[4px] pt-[32px] pb-[8px]">
              <h3
                className="truncate text-center font-bb text-[14px] leading-[17px] text-primary-white"
                style={titleColorStyle}
              >
                {formatText(item.title)}
              </h3>
            </div>
          )}
        </div>
      </MagicJump>
    </div>
  );
}

export default function OneLineTwo({
  indexData,
  moduleIndex,
  onTrack,
  onExpTrack,
  onValidExpTrack,
}: OneLineTwoProps) {
  const { deviceType } = useContext(ResponsiveContext);
  // 后台契约使用 content 包裹配置；本地 204 fixture 暂时保留当前接口的扁平结构。
  const content = indexData.content ? { ...indexData, ...indexData.content } : indexData;
  const validItems = useMemo(() => {
    const rawItems = content.jump_target || [];
    return rawItems
      .map((item, originalIndex) => ({ item, originalIndex }))
      .filter(({ item }) => Boolean(item.jump_image));
  }, [content.jump_target]);

  // 楼层 SPM 模块编码（C 位）：按楼块数据动态拼接，坑位码在此基础上追加。
  const spmModule = useHomeSpmModuleCode(indexData.component_id, indexData.id, 'home_wap');
  // Data SPM DOM 打标（C 位模块码，请求头 data-spm=1 开关）：与楼层曝光上报的模块编码同值。
  const moduleDomSpm = useSpmDomAttr(spmModule);

  if (deviceType === EPlatform.WEB || validItems.length === 0) {
    return null;
  }

  const componentId = indexData.component_id || COMPONENT_ID;
  const moduleId = String(indexData.blocks_advances_id || '');
  const moduleTitle = content.layout_title || '';
  const shouldShowHeader = Number(content.layout_title_status) === 1;
  const hasHeader = shouldShowHeader && Boolean(content.layout_title || content.layout_subtitle);
  const subtitleColor = getSubtitleColor(content);
  const titleStyle = { color: content.layout_title_color || DEFAULT_TITLE_COLOR };
  const subtitleStyle = { color: subtitleColor };

  return (
    <section
      data-component-id={componentId}
      className={`pb-[12px] ${hasHeader ? '' : 'pt-[12px]'}`}
      {...moduleDomSpm}
    >
      {hasHeader && (
        <header className="px-[12px] pt-[20px] pb-[12px] text-left">
          {content.layout_title && (
            <h3
              className="line-clamp-2 whitespace-pre-line font-bb text-[20px] leading-[24px] text-primary-black"
              style={titleStyle}
            >
              {formatText(content.layout_title)}
            </h3>
          )}
          {content.layout_subtitle && (
            <h4
              className="mt-[4px] line-clamp-3 whitespace-pre-line font-br text-[12px] font-normal leading-[14px]"
              style={subtitleStyle}
            >
              {formatText(content.layout_subtitle)}
            </h4>
          )}
        </header>
      )}
      <div className="grid grid-cols-2 gap-[6px] px-[12px]">
        {validItems.map(({ item, originalIndex }) => (
          <OneLineTwoCard
            key={item.id || `one-line-two-${originalIndex}`}
            item={item}
            originalIndex={originalIndex}
            componentId={componentId}
            moduleId={moduleId}
            moduleTitle={moduleTitle}
            moduleIndex={moduleIndex}
            spmModule={spmModule}
            onTrack={onTrack}
            onExpTrack={onExpTrack}
            onValidExpTrack={onValidExpTrack}
          />
        ))}
      </div>
    </section>
  );
}
